import { useForm, usePage, router } from "@inertiajs/react"
import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FlashMessages } from "@/components/FlashMessages"
import { FormError } from "@/components/FormError"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Workflow,
} from "lucide-react"

type RuleType =
  | "require_product"
  | "require_customer"
  | "clear_product"
  | "clear_customer"
  | "clear_tags"
  | "clear_location"
  | "require_batch"
  | "clear_batch"
import { noAutofillProps, randomColor } from "@/lib/form"
import type { AssetTypeInfo } from "@/lib/assetTypes"
import type { AppPageProps } from "@/types"

interface StageItem {
  id: string
  name: string
  color: string
  position: number
  rules: RuleType[]
  asset_types: string[]
  created_at: string
}

interface StagesPageProps extends AppPageProps {
  stages: StageItem[]
  assetTypes: AssetTypeInfo[]
  ruleTypes: RuleType[]
  kegOnlyRules: RuleType[]
}

export default function Stages() {
  const { t } = useTranslation()
  const { auth, flash, stages: serverStages, assetTypes, ruleTypes } = usePage<StagesPageProps>().props
  const [stages, setStages] = useState<StageItem[]>(serverStages)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingStage, setEditingStage] = useState<StageItem | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  // Sync local state when server props change (after create/update/delete)
  useEffect(() => {
    setStages(serverStages)
  }, [serverStages])

  const createForm = useForm({
    name: "",
    color: "#6b7280",
    rules: [] as RuleType[],
    asset_types: [] as string[],
  })

  const editForm = useForm({
    name: "",
    color: "#6b7280",
    rules: [] as RuleType[],
    asset_types: [] as string[],
  })

  function handleDragEnd(result: DropResult) {
    if (!result.destination) return

    const sourceIndex = result.source.index
    const destIndex = result.destination.index

    if (sourceIndex === destIndex) return

    const reordered = Array.from(stages)
    const [removed] = reordered.splice(sourceIndex, 1)
    reordered.splice(destIndex, 0, removed)

    // Update positions locally
    const updatedStages = reordered.map((stage, index) => ({
      ...stage,
      position: index + 1,
    }))

    setStages(updatedStages)

    // Send reorder request to server (use fetch to avoid Inertia dialog issues)
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    fetch("/stages/reorder", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: JSON.stringify({ ordered_ids: updatedStages.map(s => s.id) }),
    })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/stages", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingStage) return

    editForm.patch(`/stages/${editingStage.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingStage(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(stage: StageItem) {
    setEditingStage(stage)
    editForm.setData({
      name: stage.name,
      color: stage.color,
      rules: stage.rules || [],
      asset_types: stage.asset_types || [],
    })
  }

  function toggleRule(form: typeof createForm | typeof editForm, rule: RuleType) {
    const currentRules = form.data.rules
    if (currentRules.includes(rule)) {
      form.setData("rules", currentRules.filter(r => r !== rule))
    } else {
      form.setData("rules", [...currentRules, rule])
    }
  }

  function toggleAssetType(form: typeof createForm | typeof editForm, type: string) {
    const current = form.data.asset_types
    if (current.includes(type)) {
      form.setData("asset_types", current.filter(t => t !== type))
    } else {
      form.setData("asset_types", [...current, type])
    }
  }

  function handleDeleteStage(stage: StageItem) {
    if (confirm(t("stages.confirmDelete"))) {
      router.delete(`/stages/${stage.id}`)
    }
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("stages.title")}</h2>
            <p className="text-muted-foreground">{t("stages.subtitle")}</p>
          </div>

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open)
            if (open) {
              createForm.setData({
                name: "",
                color: randomColor(),
                rules: [],
                asset_types: ["Keg"],
              })
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("stages.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("stages.addButton")}</DialogTitle>
                  <DialogDescription>
                    {t("stages.addDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t("common.name")}</Label>
                    <Input
                      id="create-name"
                      value={createForm.data.name}
                      onChange={(e) => createForm.setData("name", e.target.value)}
                      placeholder={t("stages.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={createForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-color">{t("stages.colorLabel")}</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="create-color"
                        type="color"
                        value={createForm.data.color}
                        onChange={(e) => createForm.setData("color", e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        {...noAutofillProps}
                      />
                      <Input
                        value={createForm.data.color}
                        onChange={(e) => createForm.setData("color", e.target.value)}
                        placeholder="#6b7280"
                        className="flex-1"
                        maxLength={7}
                        {...noAutofillProps}
                      />
                    </div>
                    <FormError error={createForm.errors.color} />
                  </div>
                  <div className="space-y-3">
                    <Label>{t("stages.rulesLabel")}</Label>
                    <div className="space-y-2">
                      {ruleTypes.map((rule) => (
                        <div key={rule} className="flex items-center justify-between">
                          <Label htmlFor={`create-rule-${rule}`} className="font-normal cursor-pointer">
                            {t(`stages.rules.${rule}`)}
                          </Label>
                          <Switch
                            id={`create-rule-${rule}`}
                            checked={createForm.data.rules.includes(rule)}
                            onCheckedChange={() => toggleRule(createForm, rule)}
                          />
                        </div>
                      ))}
                    </div>
                    <FormError error={createForm.errors.rules} />
                  </div>
                  <div className="space-y-3">
                    <Label>{t("stages.assetTypesLabel")}</Label>
                    <div className="space-y-2">
                      {assetTypes.map((at) => (
                        <div key={at.key} className="flex items-center justify-between">
                          <Label htmlFor={`create-asset-type-${at.key}`} className="font-normal cursor-pointer">
                            {t(`assetTypes.${at.key}`)}
                          </Label>
                          <Switch
                            id={`create-asset-type-${at.key}`}
                            checked={createForm.data.asset_types.includes(at.type)}
                            onCheckedChange={() => toggleAssetType(createForm, at.type)}
                          />
                        </div>
                      ))}
                    </div>
                    <FormError error={createForm.errors.asset_types} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={!!editingStage} onOpenChange={(open) => !open && setEditingStage(null)}>
            <DialogContent>
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("stages.editStage")}</DialogTitle>
                  <DialogDescription>{t("stages.editDescription")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t("common.name")}</Label>
                    <Input
                      id="edit-name"
                      value={editForm.data.name}
                      onChange={(e) => editForm.setData("name", e.target.value)}
                      placeholder={t("stages.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={editForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-color">{t("stages.colorLabel")}</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="edit-color"
                        type="color"
                        value={editForm.data.color}
                        onChange={(e) => editForm.setData("color", e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        {...noAutofillProps}
                      />
                      <Input
                        value={editForm.data.color}
                        onChange={(e) => editForm.setData("color", e.target.value)}
                        placeholder="#6b7280"
                        className="flex-1"
                        maxLength={7}
                        {...noAutofillProps}
                      />
                    </div>
                    <FormError error={editForm.errors.color} />
                  </div>
                  <div className="space-y-3">
                    <Label>{t("stages.rulesLabel")}</Label>
                    <div className="space-y-2">
                      {ruleTypes.map((rule) => (
                        <div key={rule} className="flex items-center justify-between">
                          <Label htmlFor={`edit-rule-${rule}`} className="font-normal cursor-pointer">
                            {t(`stages.rules.${rule}`)}
                          </Label>
                          <Switch
                            id={`edit-rule-${rule}`}
                            checked={editForm.data.rules.includes(rule)}
                            onCheckedChange={() => toggleRule(editForm, rule)}
                          />
                        </div>
                      ))}
                    </div>
                    <FormError error={editForm.errors.rules} />
                  </div>
                  <div className="space-y-3">
                    <Label>{t("stages.assetTypesLabel")}</Label>
                    <div className="space-y-2">
                      {assetTypes.map((at) => (
                        <div key={at.key} className="flex items-center justify-between">
                          <Label htmlFor={`edit-asset-type-${at.key}`} className="font-normal cursor-pointer">
                            {t(`assetTypes.${at.key}`)}
                          </Label>
                          <Switch
                            id={`edit-asset-type-${at.key}`}
                            checked={editForm.data.asset_types.includes(at.type)}
                            onCheckedChange={() => toggleAssetType(editForm, at.type)}
                          />
                        </div>
                      ))}
                    </div>
                    <FormError error={editForm.errors.asset_types} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingStage(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stages Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Workflow className="w-5 h-5" />
              {t("stages.section")}
            </CardTitle>
            <CardDescription>{t("stages.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {stages.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("stages.noStages")}
              </p>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Table ref={tableRef}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead className="w-[60px]">{t("stages.positionLabel")}</TableHead>
                      <TableHead className="w-[60px]">{t("stages.colorLabel")}</TableHead>
                      <TableHead>{t("common.name")}</TableHead>
                      <TableHead>{t("stages.assetTypesLabel")}</TableHead>
                      <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <Droppable droppableId="stages">
                    {(provided) => (
                      <TableBody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {stages.map((stage, index) => (
                          <Draggable key={stage.id} draggableId={stage.id} index={index}>
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`${index % 2 === 1 ? "bg-muted" : ""} ${snapshot.isDragging ? "bg-accent shadow-lg" : ""}`}
                                style={{
                                  ...provided.draggableProps.style,
                                  ...(snapshot.isDragging ? {
                                    display: 'table',
                                    tableLayout: 'fixed' as const,
                                    width: tableRef.current?.offsetWidth ?? 'auto',
                                  } : {}),
                                }}
                              >
                                <TableCell className="w-[50px]">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="cursor-grab active:cursor-grabbing p-1"
                                  >
                                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                                  </div>
                                </TableCell>
                                <TableCell className="w-[60px] text-center text-muted-foreground">
                                  {stage.position}
                                </TableCell>
                                <TableCell className="w-[60px]">
                                  <div
                                    className="w-6 h-6 rounded-full border"
                                    style={{ backgroundColor: stage.color }}
                                    title={stage.color}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{stage.name}</TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {(stage.asset_types || []).map((type) => {
                                      const info = assetTypes.find(at => at.type === type)
                                      if (!info) return null
                                      return (
                                        <Badge
                                          key={type}
                                          variant="secondary"
                                          className="text-xs bg-foreground/10 text-foreground border-0"
                                        >
                                          {t(`assetTypes.${info.key}`)}
                                        </Badge>
                                      )
                                    })}
                                  </div>
                                </TableCell>
                                <TableCell className="w-[100px]">
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => openEditDialog(stage)}
                                      title={t("stages.editStage")}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeleteStage(stage)}
                                      className="text-destructive hover:text-destructive"
                                      title={t("stages.deleteStage")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </DragDropContext>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
